import React, { useState, useCallback, useMemo } from 'react';
import { FileText, BookOpen, Filter, X } from 'lucide-react';

interface HomeSectionProps {
  onProjectSelect?: (project: any) => void;
}

const HomeSection: React.FC<HomeSectionProps> = ({ onProjectSelect }) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [expandedProject, setExpandedProject] = useState<any>(null);
  const [readArticles, setReadArticles] = useState<Set<string>>(new Set());

  const filters = [
    { id: 'ai-pm', label: 'AI Product Management', icon: BookOpen },
    { id: 'meta-pm', label: 'How I Cracked Meta PM', icon: FileText },
  ];

  const allContent = useMemo(() => [
    // AI PM Content
    {
      id: 'ai-pm-fundamentals',
      type: 'ai-pm',
      title: 'AI in Product Management',
      description: 'How AI is transforming the PM role and decision-making',
      topic: 'AI-Driven Product Strategy',
      commentary: 'AI is fundamentally changing how product managers approach decision-making. From predictive analytics to automated user insights, AI tools are enabling PMs to make data-driven decisions at scale. The key is understanding which AI applications provide real value versus hype, and how to integrate them into existing product development workflows.',
      icon: BookOpen,
      tags: ['AI', 'ProductStrategy', 'DecisionMaking']
    },
    {
      id: 'ml-pm-integration',
      type: 'ai-pm',
      title: 'ML Integration in Products',
      description: 'Practical approaches to embedding ML in user-facing products',
      topic: 'Machine Learning Productization',
      commentary: 'Successfully integrating ML into products requires a different mindset than traditional software development. You need to embrace uncertainty, focus on model performance metrics, and design for continuous improvement. The biggest challenge is often managing stakeholder expectations around what ML can realistically achieve.',
      icon: BookOpen,
      tags: ['ML', 'Productization', 'Metrics']
    },
    {
      id: 'ai-ethics-pm',
      type: 'ai-pm',
      title: 'AI Ethics & Governance',
      description: 'Navigating ethical considerations in AI product development',
      topic: 'Responsible AI Development',
      commentary: 'As PMs, we have a responsibility to consider the ethical implications of AI products. This includes bias in training data, transparency in decision-making, and ensuring AI doesn\'t perpetuate harmful stereotypes. The best approach is to build ethics into the product development process from day one, not as an afterthought.',
      icon: BookOpen,
      tags: ['Ethics', 'Governance', 'Responsibility']
    },
    {
      id: 'ai-metrics',
      type: 'ai-pm',
      title: 'AI Product Metrics',
      description: 'Measuring success in AI-powered products',
      topic: 'AI Performance Measurement',
      commentary: 'Traditional product metrics don\'t always work for AI products. You need to measure model accuracy, user trust, and the business impact of AI features. The most successful AI PMs create balanced scorecards that capture both technical performance and business value.',
      icon: BookOpen,
      tags: ['Metrics', 'Analytics', 'Performance']
    },
    {
      id: 'ai-user-experience',
      type: 'ai-pm',
      title: 'AI UX Design',
      description: 'Designing interfaces that users trust and understand',
      topic: 'Human-AI Interaction',
      commentary: 'The user experience of AI products is fundamentally different. Users need to understand what the AI is doing, why it makes certain decisions, and how to correct it when it\'s wrong. Transparency and explainability are key to building user trust in AI systems.',
      icon: BookOpen,
      tags: ['UX', 'Trust', 'Transparency']
    },
    {
      id: 'ai-roadmapping',
      type: 'ai-pm',
      title: 'AI Product Roadmapping',
      description: 'Strategic planning for AI-powered product evolution',
      topic: 'AI Product Strategy',
      commentary: 'AI product roadmaps need to account for model improvement, data acquisition, and regulatory changes. The best AI PMs create flexible roadmaps that can adapt to breakthroughs in AI technology while maintaining focus on customer value.',
      icon: BookOpen,
      tags: ['Roadmapping', 'Strategy', 'Planning']
    },

    // Meta PM Content
    {
      id: 'meta-prep-strategy',
      type: 'meta-pm',
      title: 'Meta PM Interview Strategy',
      description: 'Comprehensive approach to Meta PM interview preparation',
      topic: 'Interview Preparation Framework',
      commentary: 'Meta PM interviews test multiple dimensions: product sense, execution skills, technical understanding, and leadership. The key is to structure your preparation around these pillars and practice with real Meta scenarios. Focus on demonstrating how you think about problems, not just getting the "right" answer.',
      icon: FileText,
      tags: ['Interview', 'Strategy', 'Preparation']
    },
    {
      id: 'meta-product-sense',
      type: 'meta-pm',
      title: 'Product Sense at Meta',
      description: 'Developing the product thinking that Meta looks for',
      topic: 'Meta Product Philosophy',
      commentary: 'Meta values PMs who can think at scale, understand user behavior deeply, and make bold decisions. They want people who can identify billion-dollar opportunities and execute ruthlessly. The key is showing how you balance user needs with business impact.',
      icon: FileText,
      tags: ['ProductSense', 'Meta', 'Scale']
    },
    {
      id: 'meta-execution',
      type: 'meta-pm',
      title: 'Execution at Meta Scale',
      description: 'How to demonstrate execution capabilities in Meta interviews',
      topic: 'Execution Excellence',
      commentary: 'Meta moves fast and expects PMs to drive results. They want to see how you prioritize, navigate ambiguity, and influence cross-functional teams. The best candidates show concrete examples of shipping products that had measurable impact.',
      icon: FileText,
      tags: ['Execution', 'Impact', 'Leadership']
    },
    {
      id: 'meta-technical-pm',
      type: 'meta-pm',
      title: 'Technical PM Skills',
      description: 'Technical depth required for Meta PM roles',
      topic: 'Technical Product Management',
      commentary: 'Meta expects PMs to have strong technical foundations. You don\'t need to be an engineer, but you must understand system architecture, API design, and technical tradeoffs. The key is showing how you make technical decisions that enable product goals.',
      icon: FileText,
      tags: ['Technical', 'Systems', 'Architecture']
    },
    {
      id: 'meta-case-studies',
      type: 'meta-pm',
      title: 'Meta Case Study Framework',
      description: 'Approach to solving Meta-style product cases',
      topic: 'Case Study Methodology',
      commentary: 'Meta case studies test how you structure problems and think through solutions. The best approach is to start with the user problem, identify key metrics, consider multiple solutions, and recommend a path forward with clear reasoning. Always tie back to Meta\'s business model.',
      icon: FileText,
      tags: ['CaseStudies', 'Framework', 'ProblemSolving']
    },
    {
      id: 'meta-career-growth',
      type: 'meta-pm',
      title: 'Career Growth at Meta',
      description: 'Navigating career progression as a Meta PM',
      topic: 'Professional Development',
      commentary: 'Meta offers incredible growth opportunities but expects high performance. The key is to take on increasingly complex projects, mentor others, and develop your unique area of expertise. The most successful Meta PMs build strong networks and deliver consistent results.',
      icon: FileText,
      tags: ['Career', 'Growth', 'Mentorship']
    }
  ], []);

  // Get filtered content
  const getFilteredContent = useCallback(() => {
    return allContent.filter(item => {
      const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes(item.type);
      return matchesFilters;
    });
  }, [selectedFilters, allContent]);

  const filteredContent = getFilteredContent();

  const toggleFilter = (filterId: string) => {
    setSelectedFilters(prev => 
      prev.includes(filterId) 
        ? prev.filter(f => f !== filterId)
        : [...prev, filterId]
    );
  };

  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const renderProjectCard = (project: any) => {
    const isExpanded = expandedProject?.id === project.id;
    const isRead = readArticles.has(project.id);
    
    return (
      <div
        key={project.id}
        className={`bg-white rounded-lg shadow-sm border transition-all ${
          isExpanded 
            ? 'md:col-span-2 lg:col-span-3 border-beige-600 shadow-lg' 
            : isRead
              ? 'border-beige-300 bg-beige-50/30 hover:border-beige-400 hover:shadow-md cursor-pointer'
              : 'border-beige-200 hover:border-beige-400 hover:shadow-md cursor-pointer'
        }`}
        onClick={() => {
          if (!isExpanded) {
            setExpandedProject(project);
            setReadArticles(prev => new Set(prev).add(project.id));
          }
        }}
      >
        <div className="p-6">
          {/* Read indicator */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{project.title}</h3>
              <p className="text-gray-600 text-sm">{project.description}</p>
            </div>
            {isRead && !isExpanded && (
              <div className="ml-3 flex-shrink-0">
                <div className="w-2 h-2 bg-beige-500 rounded-full" title="Already read"></div>
              </div>
            )}
          </div>
          
          {/* Expanded Content */}
          {isExpanded && (
            <div className="mt-6 pt-6 border-t border-beige-200">
              <div className="flex justify-between items-start mb-4">
                <h4 className="text-xl font-medium text-gray-900">{project.topic}</h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedProject(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <h5 className="text-lg font-medium text-gray-900 mb-3">My Commentary</h5>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {project.commentary}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Filter Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light text-gray-900 mb-6">
            
          </h1>
          <p className="text-lg text-gray-600 mb-12">
            AI PM Thinking × FAANG PM Interviews
          </p>
          
          {/* Centered Filter Cards */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-8 mb-8">
            {filters.map((filter) => {
              const Icon = filter.icon;
              const isSelected = selectedFilters.includes(filter.id);
              
              return (
                <button
                  key={filter.id}
                  onClick={() => toggleFilter(filter.id)}
                  className={`relative p-8 rounded-xl border-2 transition-all transform hover:scale-105 w-64 ${
                    isSelected
                      ? 'border-beige-600 bg-gradient-to-br from-beige-50 to-beige-100 shadow-lg'
                      : 'border-beige-200 bg-white hover:border-beige-400 hover:shadow-md'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className={`p-4 rounded-full transition-all ${
                      isSelected
                        ? 'bg-beige-600 text-white'
                        : 'bg-beige-100 text-beige-600'
                    }`}>
                      <Icon size={32} />
                    </div>
                    <span className={`font-medium text-base ${
                      isSelected ? 'text-beige-800' : 'text-gray-700'
                    }`}>
                      {filter.label}
                    </span>
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <div className="w-3 h-3 bg-beige-600 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filter Controls */}
          <div className="flex flex-col items-center space-y-3">
            {selectedFilters.length > 0 && (
              <div className="flex items-center space-x-4">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={16} className="mr-2" />
                  Clear filters
                </button>
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-beige-600">
                    {selectedFilters.length} filter{selectedFilters.length > 1 ? 's' : ''} active
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredContent.map(renderProjectCard)}
        </div>

        {/* Empty State */}
        {filteredContent.length === 0 && (
          <div className="text-center py-12">
            <Filter size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
            <p className="text-gray-600">Try adjusting your filters to see more content</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeSection;
